import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { ServiceBadge } from "@/components/dashboard/ServiceBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  subscriptionService,
  ESubscriptionStatus,
  ESubscriptionType,
  ESubType,
  EownerType,
  SubscriptionPaymentInfo,
  getSubscriptionServiceId,
} from "@/services/subscription";
import { ExtendSubscriptionModal } from "@/components/subscriptions/ExtendSubscriptionModal";
import { EditPaymentModal, EditPaymentFormData } from "@/components/payments/EditPaymentModal";
import { SupplierCombobox } from "@/components/shared/SupplierCombobox";
import { paymentService, Payment, EPaymentStatus, EPaymentMethod } from "@/services/payment";
import { Supplier, ESupplierStatus, supplierService } from "@/services/supplier";
import {
  ArrowLeft,
  Loader2,
  User,
  Phone,
  Mail,
  Calendar,
  CreditCard,
  Clock,
  DollarSign,
  FileText,
  MapPin,
  Pencil,
  RefreshCw,
  XCircle,
  Bell,
  KeyRound,
  MessageCircle,
} from "lucide-react";
import { WhatsAppChatSheet } from "@/components/whatsapp/chat/WhatsAppChatSheet";
import { formatCurrency } from "@/lib/utils";
import {
  messageTemplateService,
  EMessageTemplateType,
} from "@/services/messageTemplate";

function mapSubscriptionStatus(status: ESubscriptionStatus): "ACTIVE" | "EXPIRING" | "EXPIRED" | "CANCELLED" {
  const statusMap: Record<ESubscriptionStatus, "ACTIVE" | "EXPIRING" | "EXPIRED" | "CANCELLED"> = {
    [ESubscriptionStatus.ACTIVE]: "ACTIVE",
    [ESubscriptionStatus.EXPIRING]: "EXPIRING",
    [ESubscriptionStatus.EXPIRED]: "EXPIRED",
    [ESubscriptionStatus.CANCELLED]: "CANCELLED",
  };
  return statusMap[status] || "ACTIVE";
}

function getPaymentStatusLabel(status: EPaymentStatus): string {
  const labels: Record<EPaymentStatus, string> = {
    [EPaymentStatus.COMPLETED]: "Completado",
    [EPaymentStatus.PENDING]: "Pendiente",
    [EPaymentStatus.CANCELLED]: "Cancelado",
    [EPaymentStatus.REFUNDED]: "Reembolsado",
  };
  return labels[status] || status;
}

function getPaymentStatusColor(status: EPaymentStatus): string {
  const colors: Record<EPaymentStatus, string> = {
    [EPaymentStatus.COMPLETED]: "bg-green-100 text-green-800",
    [EPaymentStatus.PENDING]: "bg-yellow-100 text-yellow-800",
    [EPaymentStatus.CANCELLED]: "bg-red-100 text-red-800",
    [EPaymentStatus.REFUNDED]: "bg-blue-100 text-blue-800",
  };
  return colors[status] || "bg-gray-100 text-gray-800";
}

function getPaymentMethodLabel(method: EPaymentMethod): string {
  const labels: Record<EPaymentMethod, string> = {
    [EPaymentMethod.CASH]: "Efectivo",
    [EPaymentMethod.TRANSFER]: "Transferencia",
    [EPaymentMethod.NEQUI]: "Nequi",
    [EPaymentMethod.DAVIPLATA]: "Daviplata",
    [EPaymentMethod.BANCOLOMBIA]: "Bancolombia",
    [EPaymentMethod.PAYPAL]: "PayPal",
    [EPaymentMethod.BINANCE]: "Binance",
    [EPaymentMethod.YAPE]: "Yape",
  };
  return labels[method] || method;
}

export default function SubscriptionDetail() {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isEditSubscriptionOpen, setIsEditSubscriptionOpen] = useState(false);
  const [isEditPaymentOpen, setIsEditPaymentOpen] = useState(false);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isChangeSupplierOpen, setIsChangeSupplierOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedPricePlanId, setSelectedPricePlanId] = useState<string>("");
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [subscriptionForm, setSubscriptionForm] = useState({
    price: 0,
    startDate: "",
    endDate: "",
    status: ESubscriptionStatus.ACTIVE,
  });

  // Única query: el detalle incluye client, payments y subscriptionItems completos
  const {
    data: subscriptionResponse,
    isLoading,
    error: subscriptionError,
  } = useQuery({
    queryKey: ["subscription", subscriptionId],
    queryFn: () => subscriptionService.getOne(subscriptionId!),
    enabled: !!subscriptionId,
  });

  const subscription = subscriptionResponse?.data;

  // Plantillas activas para saber a cuál enviar según el tipo de mensaje
  const { data: templatesResponse } = useQuery({
    queryKey: ["message-templates", "active"],
    queryFn: () => messageTemplateService.getAll({ isActive: true }),
  });

  const templates = templatesResponse?.data || [];
  const accountInfoTemplate = templates.find(
    (tpl) => tpl.type === EMessageTemplateType.INFO_ACCOUNT,
  );
  const paymentReminderTemplate = templates.find(
    (tpl) => tpl.type === EMessageTemplateType.PAYMENT_REMINDER,
  );

  // Datos derivados directamente de la respuesta
  const item = subscription?.subscriptionItems?.[0];
  const client = subscription?.client;
  const payments = [...(subscription?.payments || [])].sort(
    (a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime(),
  );
  const subscriptionServiceId = subscription ? getSubscriptionServiceId(subscription) : undefined;

  // Planes de precio activos del proveedor seleccionado para el servicio de esta suscripción
  const { data: supplierPricePlansResponse, isLoading: isLoadingPricePlans } = useQuery({
    queryKey: ["supplier-price-plans", selectedSupplier?.id],
    queryFn: () => supplierService.getPricePlans(selectedSupplier!.id),
    enabled: !!selectedSupplier,
  });

  const availablePricePlans = (supplierPricePlansResponse?.data || []).filter(
    (plan) =>
      plan.status === ESupplierStatus.ACTIVE &&
      plan.streamingServiceId === subscriptionServiceId,
  );

  // Slot display
  const slotDisplay =
    subscription?.type === ESubType.COMBO
      ? subscription?.combo?.name || "Combo"
      : item?.subscriptionType === ESubscriptionType.SLOT
        ? item?.slot?.name || "—"
        : "Cuenta completa";

  // Mutation para actualizar suscripción
  const updateSubscriptionMutation = useMutation({
    mutationFn: (data: { price: number; startDate: string; endDate: string; status: ESubscriptionStatus }) =>
      subscriptionService.update(subscriptionId!, data),
    onSuccess: () => {
      toast.success("Suscripción actualizada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["subscription", subscriptionId] });
      setIsEditSubscriptionOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Mutation para actualizar pago
  const updatePaymentMutation = useMutation({
    mutationFn: (data: { id: string; paymentDate: string; status: EPaymentStatus; amount: number; paymentMethod?: EPaymentMethod; notes?: string }) =>
      paymentService.update(data.id, {
        paymentDate: data.paymentDate,
        status: data.status,
        amount: data.amount,
        paymentMethod: data.paymentMethod,
        notes: data.notes,
      }),
    onSuccess: () => {
      toast.success("Pago actualizado exitosamente");
      // Recargar la suscripción completa para reflejar el pago actualizado
      queryClient.invalidateQueries({ queryKey: ["subscription", subscriptionId] });
      setIsEditPaymentOpen(false);
      setSelectedPayment(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Mutation para cancelar suscripción
  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => subscriptionService.cancel(subscriptionId!),
    onSuccess: () => {
      toast.success("Suscripción cancelada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["subscription", subscriptionId] });
      queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const changeSupplierMutation = useMutation({
    mutationFn: ({ supplierId, pricePlanId }: { supplierId: number; pricePlanId: number }) =>
      subscriptionService.changeSupplier(subscriptionId!, Number(supplierId), Number(pricePlanId)),
    onSuccess: (response) => {
      toast.success("Proveedor actualizado exitosamente");
      queryClient.invalidateQueries({ queryKey: ["subscription", subscriptionId] });
      setIsChangeSupplierOpen(false);
      setSelectedSupplier(null);
      setSelectedPricePlanId("");
      const newSubscriptionId = response.data?.id;
      if (newSubscriptionId && String(newSubscriptionId) !== subscriptionId) {
        navigate(`/subscriptions/${newSubscriptionId}`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const sendReminderMutation = useMutation({
    mutationFn: (templateId: number) =>
      messageTemplateService.send(templateId, {
        subscriptionId: Number(subscriptionId),
      }),
    onSuccess: () => toast.success("Recordatorio enviado por WhatsApp"),
    onError: (error: Error) => toast.error(error.message),
  });

  const sendAccountInfoMutation = useMutation({
    mutationFn: (templateId: number) =>
      messageTemplateService.send(templateId, {
        subscriptionId: Number(subscriptionId),
      }),
    onSuccess: () => toast.success("Información de cuenta enviada por WhatsApp"),
    onError: (error: Error) => toast.error(error.message),
  });

  const handleSendReminder = () => {
    if (!paymentReminderTemplate) {
      toast.error("No hay una plantilla activa de tipo 'Recordatorio de pago'");
      return;
    }
    sendReminderMutation.mutate(paymentReminderTemplate.id);
  };

  const handleSendAccountInfo = () => {
    if (!accountInfoTemplate) {
      toast.error("No hay una plantilla activa de tipo 'Información de cuenta'");
      return;
    }
    sendAccountInfoMutation.mutate(accountInfoTemplate.id);
  };

  const handleEditSubscription = () => {
    if (subscription) {
      setSubscriptionForm({
        price: subscription.price,
        startDate: subscription.startDate.slice(0, 10),
        endDate: subscription.endDate.slice(0, 10),
        status: subscription.status,
      });
      setIsEditSubscriptionOpen(true);
    }
  };

  const handleEditPayment = (payment: SubscriptionPaymentInfo) => {
    setSelectedPayment(payment as unknown as Payment);
    setIsEditPaymentOpen(true);
  };

  const handleSaveSubscription = () => {
    updateSubscriptionMutation.mutate({
      price: Number(subscriptionForm.price),
      startDate: subscriptionForm.startDate,
      endDate: subscriptionForm.endDate,
      status: subscriptionForm.status,
    });
  };

  const handleSavePayment = (data: EditPaymentFormData) => {
    if (selectedPayment) {
      updatePaymentMutation.mutate({
        id: selectedPayment.id,
        paymentDate: data.paymentDate,
        status: data.status,
        amount: data.amount,
        paymentMethod: data.paymentMethod,
        notes: data.notes,
      });
    }
  };

  const handleCancelSubscription = () => {
    cancelSubscriptionMutation.mutate(undefined, {
      onSuccess: () => setIsCancelModalOpen(false),
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Detalle de Suscripción" subtitle="Cargando inform
      ación...">
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Cargando...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (subscriptionError || !subscription) {
    return (
      <DashboardLayout title="Detalle de Suscripción" subtitle="Error">
        <div className="flex flex-col items-center justify-center p-12 gap-4">
          <p className="text-destructive">No se pudo cargar la suscripción</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const paymentTotalsByStatus = payments.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + Number(p.amount);
    return acc;
  }, {} as Record<EPaymentStatus, number>);

  const totalPaid = paymentTotalsByStatus[EPaymentStatus.COMPLETED] || 0;

  return (
    <DashboardLayout
      title={`Detalle de Suscripción ${subscription.ownerType === EownerType.SUPPLIER ? "al proveedor" : "del cliente"}`}
      subtitle={`${client?.name || subscription.supplier.name} - ${slotDisplay}`}
    >
      <div className="space-y-6 animate-fade-in">
        {/* Header con botones de acción */}
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <div className="flex items-center gap-2">
            {subscription.ownerType !== EownerType.SUPPLIER && (
              <>
                {client?.phone && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-green-500/40 text-green-500 hover:bg-green-500/10 hover:border-green-500/60 hover:text-green-500"
                    onClick={() => setIsChatOpen(true)}
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Chat
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="border-green-500/40 text-green-500 hover:bg-green-500/10 hover:border-green-500/60 hover:text-green-500"
                  onClick={handleSendReminder}
                  disabled={sendReminderMutation.isPending || !paymentReminderTemplate}
                >
                  {sendReminderMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Bell className="mr-2 h-4 w-4" />
                  )}
                  Enviar recordatorio
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-green-500/40 text-green-500 hover:bg-green-500/10 hover:border-green-500/60 hover:text-green-500"
                  onClick={handleSendAccountInfo}
                  disabled={sendAccountInfoMutation.isPending || !accountInfoTemplate}
                >
                  {sendAccountInfoMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <KeyRound className="mr-2 h-4 w-4" />
                  )}
                  Enviar información
                </Button>
              </>
            )}
            {subscription.status !== ESubscriptionStatus.CANCELLED && (
              <>
                <Button variant="default" size="sm" onClick={() => setIsExtendModalOpen(true)}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Extender
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setIsCancelModalOpen(true)}>
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancelar
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Grid de tarjetas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tarjeta de Suscripción */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Información de la Suscripción
                </CardTitle>
                <Button variant="outline" size="sm" onClick={handleEditSubscription}>
                  <Pencil className="h-4 w-4 mr-1" />
                  Editar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Servicio</p>
                  <div className="mt-1">
                    {subscription.type === ESubType.COMBO ? (
                      <ServiceBadge icon="📦" name={subscription?.combo?.name || "Combo"} />
                    ) : (
                      <ServiceBadge
                        icon={item?.service?.icon || ""}
                        name={item?.service?.name || "—"}
                      />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <div className="mt-1">
                    <StatusBadge status={mapSubscriptionStatus(subscription.status)} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Slot</p>
                    <p className="font-medium">{slotDisplay}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Precio</p>
                    <p className="font-medium">${formatCurrency(subscription.price)}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Fecha de inicio</p>
                    <p className="font-medium">
                      {new Date(subscription.startDate).toLocaleDateString("es-CO")}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Fecha de fin</p>
                    <p className="font-medium">
                      {new Date(subscription.endDate).toLocaleDateString("es-CO")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Días restantes</p>
                  <p className="font-medium">
                    {subscription.daysRemaining > 0
                      ? `${subscription.daysRemaining} días`
                      : "Vencido"}
                  </p>
                </div>
              </div>

              {subscription.type !== ESubType.COMBO && (
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-muted-foreground">Cuenta de streaming</p>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium truncate">{item?.streamingAccount?.email || "-"}</p>
                      {item?.streamingAccount?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-shrink-0"
                          onClick={() => navigate(`/accounts/${item.streamingAccount!.id}`)}
                        >
                          <ArrowLeft className="h-4 w-4 mr-1 rotate-180" />
                          Ver cuenta
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tarjeta de Cliente o Proveedor según ownerType */}
          {subscription.ownerType === EownerType.SUPPLIER ? (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="h-5 w-5 text-primary" />
                    Información del Proveedor
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedSupplier(null);
                        setIsChangeSupplierOpen(true);
                      }}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Cambiar proveedor
                    </Button>
                    {subscription.supplier && (
                      <Button variant="outline" size="sm" onClick={() => navigate(`/suppliers/${subscription.supplier.id}`)}>
                        <ArrowLeft className="h-4 w-4 mr-1 rotate-180" />
                        Ver proveedor
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {subscription.supplier ? (
                  <>
                    <div className="flex items-start gap-2">
                      <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Nombre</p>
                        <p className="font-medium">{subscription.supplier.name}</p>
                      </div>
                    </div>

                    {subscription.supplier.phone && (
                      <div className="flex items-start gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Teléfono</p>
                          <p className="font-medium">{subscription.supplier.phone}</p>
                        </div>
                      </div>
                    )}

                    {subscription.supplier.email && (
                      <div className="flex items-start gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Email</p>
                          <p className="font-medium">{subscription.supplier.email}</p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-start gap-2">
                        <CreditCard className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Estado</p>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              subscription.supplier.status === ESupplierStatus.ACTIVE
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {subscription.supplier.status === ESupplierStatus.ACTIVE
                              ? "Activo"
                              : "Inactivo"}
                          </span>
                        </div>
                      </div>
                      {subscription.supplier.score !== undefined &&
                        subscription.supplier.score !== null && (
                          <div className="flex items-start gap-2">
                            <DollarSign className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div>
                              <p className="text-sm text-muted-foreground">Puntuación</p>
                              <p className="font-medium">{subscription.supplier.score}</p>
                            </div>
                          </div>
                        )}
                    </div>

                    {subscription.supplier.createdAt && (
                      <div className="flex items-start gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Proveedor desde</p>
                          <p className="font-medium">
                            {new Date(subscription.supplier.createdAt).toLocaleDateString("es-CO")}
                          </p>
                        </div>
                      </div>
                    )}

                    {subscription.supplier.notes && (
                      <div className="flex items-start gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Notas</p>
                          <p className="font-medium">{subscription.supplier.notes}</p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground">No se encontró información del proveedor</p>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="h-5 w-5 text-primary" />
                    Información del Cliente
                  </CardTitle>
                  {client && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/clients/${client.id}`)}>
                      <ArrowLeft className="h-4 w-4 mr-1 rotate-180" />
                      Ver cliente
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {client ? (
                  <>
                    <div className="flex items-start gap-2">
                      <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Nombre</p>
                        <p className="font-medium">{client.name}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Teléfono</p>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{client.phone}</p>
                          {client.phone && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-success"
                              onClick={() => setIsChatOpen(true)}
                              title="Abrir chat de WhatsApp"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {client.email && (
                      <div className="flex items-start gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Email</p>
                          <p className="font-medium">{client.email}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Servicios activos</p>
                        <p className="font-medium">{client.activeServicesCount}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Valor mensual</p>
                        <p className="font-medium">${formatCurrency(Number(client.monthlyValue))}</p>
                      </div>
                    </div>

                    {client.notes && (
                      <div className="flex items-start gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Notas</p>
                          <p className="font-medium">{client.notes}</p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground">No se encontró información del cliente</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tarjeta de Servicios del Combo */}
        {subscription.type === ESubType.COMBO && subscription.subscriptionItems?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="text-lg">📦</span>
                Servicios del Combo
                <span className="text-sm font-normal text-muted-foreground">
                  — {subscription?.combo?.name || "Combo"}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {subscription.subscriptionItems.map((si) => (
                  <div
                    key={si.id}
                    className="flex items-start gap-3 p-3 rounded-lg border bg-muted/20"
                  >
                    <span
                      className="w-9 h-9 rounded-md flex items-center justify-center text-xl flex-shrink-0"
                      style={{
                        backgroundColor: si.service?.color
                          ? `${si.service.color}20`
                          : "hsl(var(--muted))",
                      }}
                    >
                      {si.service?.icon || "📺"}
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{si.service?.name || "—"}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {si.streamingAccount?.email || "—"}
                      </p>
                      {si.slot && (
                        <p className="text-xs text-muted-foreground">
                          Slot: <span className="font-medium text-foreground">{si.slot.name}</span>
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabla de Pagos */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="h-5 w-5 text-primary" />
                Historial de Pagos
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                {(Object.values(EPaymentStatus) as EPaymentStatus[])
                  .filter((status) => paymentTotalsByStatus[status] !== undefined)
                  .map((status) => (
                    <span
                      key={status}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(status)}`}
                    >
                      {getPaymentStatusLabel(status)}: ${formatCurrency(paymentTotalsByStatus[status])}
                    </span>
                  ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No hay pagos registrados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Monto</TableHead>
                      <TableHead>Método</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Notas</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>
                          {new Date(payment.paymentDate).toLocaleDateString("es-CO")}
                        </TableCell>
                        <TableCell className="font-medium">
                          ${formatCurrency(Number(payment.amount))}
                        </TableCell>
                        <TableCell>{getPaymentMethodLabel(payment.paymentMethod)}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(payment.status)}`}>
                            {getPaymentStatusLabel(payment.status)}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {payment.notes || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditPayment(payment)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal Editar Suscripción */}
      <Dialog open={isEditSubscriptionOpen} onOpenChange={setIsEditSubscriptionOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Editar Suscripción</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="price">Precio</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={subscriptionForm.price}
                onChange={(e) =>
                  setSubscriptionForm((prev) => ({
                    ...prev,
                    price: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Fecha de inicio</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={subscriptionForm.startDate}
                  onChange={(e) =>
                    setSubscriptionForm((prev) => ({ ...prev, startDate: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">Fecha de fin</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={subscriptionForm.endDate}
                  onChange={(e) =>
                    setSubscriptionForm((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Estado</Label>
              <Select
                value={subscriptionForm.status}
                onValueChange={(value) =>
                  setSubscriptionForm((prev) => ({ ...prev, status: value as ESubscriptionStatus }))
                }
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ESubscriptionStatus.ACTIVE}>Activo</SelectItem>
                  <SelectItem value={ESubscriptionStatus.EXPIRING}>Por vencer</SelectItem>
                  <SelectItem value={ESubscriptionStatus.EXPIRED}>Vencido</SelectItem>
                  <SelectItem value={ESubscriptionStatus.CANCELLED}>Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditSubscriptionOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveSubscription} disabled={updateSubscriptionMutation.isPending}>
              {updateSubscriptionMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Pago */}
      <EditPaymentModal
        open={isEditPaymentOpen}
        onOpenChange={setIsEditPaymentOpen}
        payment={selectedPayment}
        onSave={handleSavePayment}
        isPending={updatePaymentMutation.isPending}
      />

      {/* Modal Extender Suscripción */}
      <ExtendSubscriptionModal
        open={isExtendModalOpen}
        onOpenChange={setIsExtendModalOpen}
        subscription={subscription}
      />

      {/* Modal Cambiar Proveedor */}
      <Dialog open={isChangeSupplierOpen} onOpenChange={setIsChangeSupplierOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Cambiar Proveedor</DialogTitle>
            <DialogDescription>
              Selecciona el nuevo proveedor para esta suscripción.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Proveedor</Label>
              <SupplierCombobox
                value={selectedSupplier}
                onChange={(supplier) => {
                  setSelectedSupplier(supplier);
                  setSelectedPricePlanId("");
                }}
                disabled={changeSupplierMutation.isPending}
              />
            </div>
            {selectedSupplier && (
              <div className="space-y-2">
                <Label htmlFor="pricePlan">Plan de precio</Label>
                <Select
                  value={selectedPricePlanId}
                  onValueChange={setSelectedPricePlanId}
                  disabled={changeSupplierMutation.isPending || isLoadingPricePlans}
                >
                  <SelectTrigger id="pricePlan">
                    <SelectValue
                      placeholder={
                        isLoadingPricePlans
                          ? "Cargando planes..."
                          : "Selecciona un plan de precio"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePricePlans.length === 0 ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        No hay planes activos para este servicio
                      </div>
                    ) : (
                      availablePricePlans.map((plan) => (
                        <SelectItem key={plan.id} value={String(plan.id)}>
                          {plan.name} — ${formatCurrency(plan.price)} / {plan.duration} días
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsChangeSupplierOpen(false)}
              disabled={changeSupplierMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={() =>
                selectedSupplier &&
                selectedPricePlanId &&
                changeSupplierMutation.mutate({
                  supplierId: selectedSupplier.id,
                  pricePlanId: Number(selectedPricePlanId),
                })
              }
              disabled={!selectedSupplier || !selectedPricePlanId || changeSupplierMutation.isPending}
            >
              {changeSupplierMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Cancelación */}
      <Dialog open={isCancelModalOpen} onOpenChange={setIsCancelModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Cancelar Suscripción</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas cancelar esta suscripción? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCancelModalOpen(false)}
              disabled={cancelSubscriptionMutation.isPending}
            >
              Volver
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancelSubscriptionMutation.isPending}
            >
              {cancelSubscriptionMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirmar Cancelación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {client?.phone && (
        <WhatsAppChatSheet
          open={isChatOpen}
          onOpenChange={setIsChatOpen}
          phone={client.phone}
          name={client.name}
        />
      )}
    </DashboardLayout>
  );
}
